import { useEffect, useState, type CSSProperties } from 'react';
import { useTranslation } from '../contexts/TranslationContext';
import { FadeSwitch } from './FadeSwitch';
import { ConsultantThinkingNucleus } from './ConsultantThinkingNucleus';
import './ConsultantLoadingPanel.css';

interface ConsultantLoadingPanelProps {
  isStreaming: boolean;
  hasAttachments?: boolean;
  isActive?: boolean;
  titleId?: string;
  /** Temporary development-only timeline preview; ignored in production. */
  previewElapsedSeconds?: number;
  /** Kept temporarily for callers using the former presentation variants. */
  variant?: 'illustrated' | 'minimal';
}

const PHASE_KEYS = ['received', 'context', 'corpus', 'passages', 'references', 'sources', 'structure'] as const;
const VIRTUAL_PHASE_DURATION_MS = 4200;
const LONG_WAIT_DURATION_MS = 30000;
const FINAL_ADJUSTMENTS_DURATION_MS = 60000;
const PROGRESS_REVEAL_DELAY_MS = 3000;
const PROGRESS_FILL_DURATION_MS = 24000;
const STEP_TRANSITION_MS = 1100;

export function ConsultantLoadingPanel({ isStreaming, isActive = true, titleId, previewElapsedSeconds, hasAttachments = false }: ConsultantLoadingPanelProps) {
  const { t } = useTranslation();
  const [virtualPhase, setVirtualPhase] = useState(0);
  const [waitStage, setWaitStage] = useState<'preparation' | 'analysis' | 'adjustments'>('preparation');
  const [progressStage, setProgressStage] = useState<'hidden' | 'progressing' | 'complete'>('hidden');
  const phaseCount = PHASE_KEYS.length + (hasAttachments ? 1 : 0);
  const phase = isStreaming ? phaseCount : virtualPhase;
  const previewElapsedMs = import.meta.env.DEV && typeof previewElapsedSeconds === 'number'
    && Number.isFinite(previewElapsedSeconds) && previewElapsedSeconds >= 0
    ? Math.min(previewElapsedSeconds * 1000, FINAL_ADJUSTMENTS_DURATION_MS) : 0;
  const progressStartsAt = LONG_WAIT_DURATION_MS + PROGRESS_REVEAL_DELAY_MS;
  const progressElapsedMs = Math.max(0, Math.min(previewElapsedMs - progressStartsAt, PROGRESS_FILL_DURATION_MS));

  useEffect(() => {
    if (isStreaming || !isActive) return;

    setVirtualPhase(Math.min(Math.floor(previewElapsedMs / VIRTUAL_PHASE_DURATION_MS), phaseCount - 1));
    setWaitStage(previewElapsedMs >= FINAL_ADJUSTMENTS_DURATION_MS ? 'adjustments'
      : previewElapsedMs >= LONG_WAIT_DURATION_MS ? 'analysis' : 'preparation');
    setProgressStage(previewElapsedMs >= progressStartsAt + PROGRESS_FILL_DURATION_MS ? 'complete'
      : previewElapsedMs >= progressStartsAt ? 'progressing' : 'hidden');
    const timers: number[] = [];
    const schedule = (callback: () => void, thresholdMs: number) => {
      if (thresholdMs > previewElapsedMs) timers.push(window.setTimeout(callback, thresholdMs - previewElapsedMs));
    };
    // Narrative preparation milestones; only incoming content establishes streaming.
    Array.from({ length: phaseCount - 1 }).forEach((_, index) => schedule(
      () => setVirtualPhase(index + 1), VIRTUAL_PHASE_DURATION_MS * (index + 1),
    ));
    schedule(() => setWaitStage('analysis'), LONG_WAIT_DURATION_MS);
    schedule(() => setWaitStage('adjustments'), FINAL_ADJUSTMENTS_DURATION_MS);
    schedule(() => setProgressStage('progressing'), progressStartsAt);
    schedule(() => setProgressStage('complete'), progressStartsAt + PROGRESS_FILL_DURATION_MS);

    return () => timers.forEach((timer) => window.clearTimeout(timer));
  }, [isStreaming, isActive, previewElapsedMs, progressStartsAt, phaseCount]);

  const step = isStreaming
    ? t('consultantLoading.streaming')
    : waitStage === 'adjustments'
      ? t('consultantLoading.finalAdjustments')
      : waitStage === 'analysis'
        ? t('consultantLoading.longWait')
        : hasAttachments && phase === 0 ? t('questionAttachments.indexingStep')
        : t(`consultantLoading.phases.${PHASE_KEYS[phase - (hasAttachments ? 1 : 0)]}`);
  const showWaitingNote = isActive && !isStreaming && waitStage === 'adjustments';

  return (
    <section
      className="consultant-loading consultant-loading--nucleus"
      data-variant="nucleus"
      data-active={isActive}
      aria-label={t('consultantLoading.region')}
      data-phase={phase}
      data-waiting={phase === phaseCount - 1}
      data-streaming={isStreaming}
      data-late-wait={showWaitingNote}
      data-progress={isActive && !isStreaming ? progressStage : 'hidden'}
      data-progress-state={progressStage}
      style={{
        '--pm-progress-duration': `${PROGRESS_FILL_DURATION_MS}ms`,
        '--pm-progress-elapsed': `-${progressElapsedMs}ms`,
        '--pm-step-duration': `${STEP_TRANSITION_MS}ms`,
      } as CSSProperties}
    >
      <h2 id={titleId} className="pm-heading">{t('consultantLoading.region')}</h2>
      <div className="pm-nucleus-region" aria-hidden="true">
        <ConsultantThinkingNucleus isActive={isActive && !isStreaming} />
      </div>
      <div className="pm-stage" aria-hidden="true">
        <FadeSwitch
          viewKey={isStreaming ? 'streaming' : waitStage === 'preparation' ? String(phase) : waitStage}
          childClassName="pm-step"
          enterAnimationClassName="consultant-step-enter"
          exitAnimationClassName="consultant-step-exit"
          durationMs={STEP_TRANSITION_MS}
        >
          <span className="pm-step-label">{step}</span>
        </FadeSwitch>
      </div>
      <div className="pm-wait-progress" aria-hidden="true">
        <div className="pm-wait-progress-track"><span className="pm-wait-progress-fill" /></div>
      </div>
      <p className="pm-wait-note" aria-hidden="true"><span>{t('consultantLoading.stillWorking')}</span></p>
      <span className="sr-only" role="status" aria-live="polite" aria-atomic="true">
        {showWaitingNote ? `${step} ${t('consultantLoading.stillWorking')}` : step}
      </span>
    </section>
  );
}
